import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { userAPI, meetingAPI, actionPointAPI } from '../Services/apiService';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};



export const DataProvider = ({ children }) => {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);

  // Separate loading states for progressive rendering
  const [loading, setLoading] = useState(true); // Critical data (meetings, users)
  const [analyticsLoading, setAnalyticsLoading] = useState(true); // Analytics data (action points)
  const { user, loading: authLoading } = useAuth();


  // Clear any old mock data from localStorage
  useEffect(() => {
    localStorage.removeItem('meettrack_meetings');
    localStorage.removeItem('meettrack_users');
  }, []);

  useEffect(() => {
    // AbortController so re-fires don't write stale data over a fresh load
    const controller = new AbortController();
    const { signal } = controller;

    const loadData = async () => {
      // Wait for auth to finish loading
      if (authLoading) return;

      if (!user?.token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setAnalyticsLoading(true);

        // STEP 1: Load critical data first (meetings, users) - FAST
        const [meetingsResponse, usersResponse] = await Promise.all([
          meetingAPI.getMeetings(),
          userAPI.getUsers({ include_vendors: true })
        ]);

        // Bail out if this effect cycle was cancelled before we set state
        if (signal.aborted) return;

        setMeetings(meetingsResponse.data || []);
        setUsers(usersResponse.data || []);

        localStorage.setItem('meettrack_meetings', JSON.stringify(meetingsResponse.data || []));
        localStorage.setItem('meettrack_users', JSON.stringify(usersResponse.data || []));

        setLoading(false);

        // STEP 2: Load analytics data in background (doesn't block UI)
        if (user?.role === 'admin') {
          try {
            const [tasksResponse, reviewResponse] = await Promise.all([
              actionPointAPI.getAllActionPointsAdmin({
                tab: 'all',
                page: 1,
                page_size: 100
              }),
              actionPointAPI.getReviewItems({ page: 1, page_size: 1 }).catch(() => null)
            ]);

            if (signal.aborted) return;

            const tasksData = tasksResponse.data?.items || [];
            setTasks(tasksData);

            if (reviewResponse?.data) {
              const reviewData = reviewResponse.data;
              const count = reviewData.counts?.submitted_for_review ?? reviewData.total ?? 0;
              setReviewCount(count);
            } else {
              setReviewCount(0);
            }
          } catch (taskError) {
            if (!signal.aborted) {
              console.error('Failed to load tasks for analytics:', taskError);
              setTasks([]);
              setReviewCount(0);
            }
          } finally {
            if (!signal.aborted) setAnalyticsLoading(false);
          }
        } else {
          setTasks([]);
          setReviewCount(0);
          setAnalyticsLoading(false);
        }

      } catch (error) {
        if (signal.aborted) return; // Ignore errors from cancelled requests

        console.error('Failed to load data from APIs:', error);

        // Fallback to localStorage — guard against corrupted stored data
        try {
          const storedMeetings = localStorage.getItem('meettrack_meetings');
          const storedUsers = localStorage.getItem('meettrack_users');
          if (storedMeetings && storedUsers) {
            setMeetings(JSON.parse(storedMeetings));
            setUsers(JSON.parse(storedUsers));
          } else {
            setMeetings([]);
            setUsers([]);
          }
        } catch {

          localStorage.removeItem('meettrack_meetings');
          localStorage.removeItem('meettrack_users');
          setMeetings([]);
          setUsers([]);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
          if (user?.role !== 'admin') setAnalyticsLoading(false);
        }
      }
    };

    loadData();

    // Listen for user status changes and refresh users list
    const handleUserStatusChanged = async () => {
      if (!user?.token) return;
      try {
        const response = await userAPI.getUsers({ include_vendors: true });
        setUsers(response.data || []);
        localStorage.setItem('meettrack_users', JSON.stringify(response.data || []));
      } catch (error) {
        console.error('Failed to refresh users after status change:', error);
      }
    };

    window.addEventListener('userStatusChanged', handleUserStatusChanged);

    return () => {
      controller.abort();
      window.removeEventListener('userStatusChanged', handleUserStatusChanged);
    };
  }, [user?.token, authLoading]);

  // Refresh tasks function - only for admin users
  const refreshTasks = useCallback(async () => {
    if (!user?.token) return;

    // Only admin can access admin task endpoint
    if (user?.role !== 'admin') {
      setTasks([]);
      setReviewCount(0);
      return;
    }

    try {
      setAnalyticsLoading(true);
      const [tasksResponse, reviewResponse] = await Promise.all([
        actionPointAPI.getAllActionPointsAdmin({
          tab: 'all',
          page: 1,
          page_size: 100  // Limit for better performance
        }),
        actionPointAPI.getReviewItems({ page: 1, page_size: 1 }).catch(() => null)
      ]);
      const tasksData = tasksResponse.data?.items || [];
      setTasks(tasksData);

      // Get review count from the review API response
      if (reviewResponse?.data) {
        const reviewData = reviewResponse.data;
        const count = reviewData.counts?.submitted_for_review ?? reviewData.total ?? 0;
        setReviewCount(count);
      } else {
        setReviewCount(0);
      }
    } catch (error) {
      console.error('Failed to refresh tasks:', error);
      setTasks([]);
      setReviewCount(0);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [user?.token, user?.role]);

  // Save to localStorage whenever data changes
  useEffect(() => {
    if (!loading) {
      localStorage.setItem('meettrack_meetings', JSON.stringify(meetings));
    }
  }, [meetings, loading]);

  useEffect(() => {
    if (!loading) {
      localStorage.setItem('meettrack_users', JSON.stringify(users));
    }
  }, [users, loading]);

  // Refresh tasks periodically (every 30 seconds) for admin users only
  useEffect(() => {
    if (!user?.token || loading || user?.role !== 'admin') return;

    const interval = setInterval(() => {
      refreshTasks();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [user?.token, user?.role, loading, refreshTasks]);

  // Listen for task status changes to refresh review count immediately
  useEffect(() => {
    if (!user?.token || user?.role !== 'admin') return;

    const handleTaskStatusChange = () => {
      refreshTasks();
    };

    window.addEventListener('taskStatusChanged', handleTaskStatusChange);

    return () => {
      window.removeEventListener('taskStatusChanged', handleTaskStatusChange);
    };
  }, [user?.token, user?.role, refreshTasks]);

  // Meeting operations
  const createMeeting = async (meetingData) => {
    try {
      const response = await meetingAPI.createMeeting(meetingData);
      const newMeeting = response.data;
      setMeetings(prev => [...prev, newMeeting]);
      return newMeeting;
    } catch (error) {
      console.error('Failed to create meeting:', error);
      throw error;
    }
  };

  const updateMeeting = async (meetingId, updates) => {
    try {
      const response = await meetingAPI.updateMeeting(meetingId, updates);
      const updatedMeeting = response.data;
      setMeetings(prev => prev.map(meeting =>
        meeting.id === meetingId ? updatedMeeting : meeting
      ));
      return updatedMeeting;
    } catch (error) {
      console.error('Failed to update meeting:', error);
      throw error;
    }
  };

  const deleteMeeting = async (meetingId) => {
    try {
      await meetingAPI.deleteMeeting(meetingId);
      setMeetings(prev => prev.filter(meeting => meeting.id !== meetingId));
    } catch (error) {
      console.error('Failed to delete meeting:', error);
      throw error;
    }
  };



  const updateActionPoint = (meetingId, actionPointId, updates) => {
    setMeetings(prev => prev.map(meeting => {
      if (meeting.id === meetingId) {
        return {
          ...meeting,
          actionPoints: meeting.actionPoints.map(ap =>
            ap.id === actionPointId ? { ...ap, ...updates } : ap
          )
        };
      }
      return meeting;
    }));
  };





  // Analytics helpers
  const getTaskAnalytics = () => {
    // Use tasks from state if available (for admin), otherwise fall back to meetings.actionPoints
    let allTasks = [];

    if (tasks.length > 0) {
      // Use tasks from API (admin view)
      allTasks = tasks;
      const completed = allTasks.filter(task => task.status === 'completed').length;
      const total = allTasks.length;
      const overdue = allTasks.filter(task => {
        if (!task.due_date) return false;
        // Only pending and rejected tasks are overdue (not submitted_for_review)
        const overdueStatuses = ['pending', 'rejected_in_review'];
        if (!overdueStatuses.includes(task.status)) return false;

        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Overdue only AFTER due date passes (not on due date)
        return today > dueDate;
      }).length;

      return {
        total,
        completed,
        incomplete: total - completed,
        overdue,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
      };
    } else {
      // Fallback to meetings.actionPoints (for backward compatibility)
      allTasks = meetings.flatMap(meeting => meeting.actionPoints || []);
      const completed = allTasks.filter(task => task.completed).length;
      const total = allTasks.length;
      const overdue = allTasks.filter(task => {
        if (!task.dueDate) return false;
        const dueDate = new Date(task.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Overdue only AFTER due date passes (not on due date)
        return !task.completed && today > dueDate;
      }).length;

      return {
        total,
        completed,
        incomplete: total - completed,
        overdue,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
      };
    }
  };

  const getDepartmentStats = () => {
    const departments = {};
    users.forEach(user => {
      if (!departments[user.department]) {
        departments[user.department] = { total: 0, employees: [] };
      }
      departments[user.department].total++;
      departments[user.department].employees.push(user);
    });
    return departments;
  };

  const getMeetingStats = () => {
    const total = meetings.length;
    const upcoming = meetings.filter(m => m.status === 'upcoming').length;
    const completed = meetings.filter(m => m.status === 'completed').length;

    return { total, upcoming, completed };
  };

  const getReviewAnalytics = () => {
    return {
      total: reviewCount,
      pending: reviewCount,
      overdue: 0
    };
  };



  const value = {
    meetings,
    users,
    tasks,
    loading, // Critical data loading state
    analyticsLoading, // Analytics data loading state
    createMeeting,
    updateMeeting,
    deleteMeeting,
    updateActionPoint,

    getTaskAnalytics,
    getDepartmentStats,
    getMeetingStats,
    getReviewAnalytics,
    refreshTasks, // Expose refreshTasks for manual refresh
  };


  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};